/**
 * Dashboard Service for Developer Console, Analytics, and Admin Operations
 * Provides comprehensive management interfaces for functions and platform operations
 */

import { apiService, ApiResponse } from './api';

export interface FunctionMetadata {
  id?: string;
  rufid: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  tags: string[];
  version: string;
  sourceCode: string;
  runtime: 'python' | 'javascript' | 'typescript' | 'go' | 'rust';
  dependencies: string[];
  environment: Record<string, string>;
  pricing: {
    model: 'free' | 'paid' | 'subscription' | 'usage';
    price?: number;
    currency: string;
    billingCycle?: 'monthly' | 'annual';
    freeLimit?: number;
    usagePrice?: number;
  };
  documentation: {
    readme: string;
    examples: Array<{
      title: string;
      description: string;
      input: any;
      output: any;
      code: string;
    }>;
    changelog: string;
  };
  testing: {
    testCases: Array<{
      name: string;
      input: any;
      expectedOutput: any;
      description: string;
    }>;
  };
  visibility: 'public' | 'private' | 'unlisted';
  status: 'draft' | 'published' | 'archived';
}

export interface PublishedFunction extends FunctionMetadata {
  id: string;
  stats: {
    downloads: number;
    rating: number;
    reviewCount: number;
    usageCount: number;
    revenue: number;
  };
  deployment: {
    status: 'deploying' | 'deployed' | 'failed';
    url: string;
    lastDeployment: string;
    errorMessage?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DeveloperAnalytics {
  overview: {
    totalFunctions: number;
    totalDownloads: number;
    totalRevenue: number;
    avgRating: number;
    activeUsers: number;
  };
  functionsPerformance: Array<{
    functionId: string;
    name: string;
    downloads: number;
    revenue: number;
    rating: number;
    usageGrowth: number;
  }>;
  revenueByMonth: Array<{
    month: string;
    revenue: number;
    downloads: number;
  }>;
  userEngagement: {
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
    retention: number;
  };
  technicalMetrics: {
    avgResponseTime: number;
    errorRate: number;
    uptime: number;
    apiCallsPerDay: number;
  };
}

export interface ApiKey {
  id: string;
  name: string;
  key: string; // Only shown once during creation
  keyPreview: string; // Masked version for display
  permissions: string[];
  lastUsed?: string;
  usageCount: number;
  rateLimit: {
    requests: number;
    period: 'minute' | 'hour' | 'day';
  };
  expiresAt?: string;
  active: boolean;
  createdAt: string;
}

export interface UserAccount {
  id: string;
  email: string;
  username: string;
  displayName: string;
  avatar?: string;
  role: 'user' | 'developer' | 'admin';
  verified: boolean;
  subscription?: {
    plan: string;
    status: string;
    currentPeriodEnd: string;
  };
  profile: {
    bio?: string;
    website?: string;
    location?: string;
    company?: string;
    github?: string;
    twitter?: string;
  };
  preferences: {
    emailNotifications: boolean;
    marketingEmails: boolean;
    theme: 'light' | 'dark' | 'auto';
    language: string;
    timezone: string;
  };
  stats: {
    functionsPublished: number;
    functionsOwned: number;
    totalDownloads: number;
    joinedAt: string;
  };
  security: {
    twoFactorEnabled: boolean;
    lastLogin: string;
    loginCount: number;
  };
}

export interface AdminMetrics {
  platform: {
    totalUsers: number;
    totalFunctions: number;
    totalTransactions: number;
    totalRevenue: number;
    activeUsers: number;
    functionsPublishedThisMonth: number;
  };
  usage: {
    dailyActiveUsers: Array<{ date: string; count: number }>;
    functionExecutions: Array<{ date: string; count: number }>;
    revenueByDay: Array<{ date: string; revenue: number }>;
  };
  health: {
    systemStatus: 'healthy' | 'degraded' | 'down';
    services: Array<{
      name: string;
      status: 'up' | 'down' | 'degraded';
      responseTime: number;
      uptime: number;
    }>;
    infrastructure: {
      cpu: number;
      memory: number;
      storage: number;
      network: number;
    };
  };
}

class DashboardService {
  private readonly basePath = '/api/v1/dashboard';

  // Function Management
  async createFunction(metadata: Omit<FunctionMetadata, 'id'>): Promise<ApiResponse<PublishedFunction>> {
    return apiService.post(`${this.basePath}/functions`, metadata);
  }

  async updateFunction(functionId: string, updates: Partial<FunctionMetadata>): Promise<ApiResponse<PublishedFunction>> {
    return apiService.patch(`${this.basePath}/functions/${functionId}`, updates);
  }

  async deleteFunction(functionId: string): Promise<ApiResponse<void>> {
    return apiService.delete(`${this.basePath}/functions/${functionId}`);
  }

  async getMyFunctions(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    functions: PublishedFunction[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    return apiService.get(`${this.basePath}/functions?page=${page}&limit=${limit}`);
  }

  async getFunctionDetails(functionId: string): Promise<ApiResponse<PublishedFunction>> {
    return apiService.get(`${this.basePath}/functions/${functionId}`);
  }

  async publishFunction(functionId: string): Promise<ApiResponse<PublishedFunction>> {
    return apiService.post(`${this.basePath}/functions/${functionId}/publish`);
  }

  async unpublishFunction(functionId: string): Promise<ApiResponse<PublishedFunction>> {
    return apiService.post(`${this.basePath}/functions/${functionId}/unpublish`);
  }

  async deployFunction(functionId: string, version?: string): Promise<ApiResponse<{
    deploymentId: string;
    status: string;
    logs: string[];
  }>> {
    return apiService.post(`${this.basePath}/functions/${functionId}/deploy`, { version });
  }

  async getFunctionLogs(functionId: string, limit: number = 100): Promise<ApiResponse<Array<{
    timestamp: string;
    level: 'info' | 'warn' | 'error';
    message: string;
    metadata?: Record<string, any>;
  }>>> {
    return apiService.get(`${this.basePath}/functions/${functionId}/logs?limit=${limit}`);
  }

  // Analytics and Performance
  async getDeveloperAnalytics(period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<ApiResponse<DeveloperAnalytics>> {
    return apiService.get(`${this.basePath}/analytics?period=${period}`);
  }

  async getFunctionAnalytics(functionId: string, period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<ApiResponse<{
    usage: Array<{ date: string; calls: number; uniqueUsers: number }>;
    performance: Array<{ date: string; avgResponseTime: number; errorRate: number }>;
    revenue: Array<{ date: string; revenue: number; transactions: number }>;
    users: {
      total: number;
      new: number;
      returning: number;
      countries: Array<{ country: string; count: number }>;
    };
  }>> {
    return apiService.get(`${this.basePath}/functions/${functionId}/analytics?period=${period}`);
  }

  // API Key Management
  async getApiKeys(): Promise<ApiResponse<ApiKey[]>> {
    return apiService.get(`${this.basePath}/api-keys`);
  }

  async createApiKey(config: {
    name: string;
    permissions: string[];
    rateLimit?: { requests: number; period: 'minute' | 'hour' | 'day' };
    expiresIn?: number; // days
  }): Promise<ApiResponse<ApiKey>> {
    return apiService.post(`${this.basePath}/api-keys`, config);
  }

  async updateApiKey(keyId: string, updates: {
    name?: string;
    permissions?: string[];
    rateLimit?: { requests: number; period: 'minute' | 'hour' | 'day' };
    active?: boolean;
  }): Promise<ApiResponse<ApiKey>> {
    return apiService.patch(`${this.basePath}/api-keys/${keyId}`, updates);
  }

  async deleteApiKey(keyId: string): Promise<ApiResponse<void>> {
    return apiService.delete(`${this.basePath}/api-keys/${keyId}`);
  }

  async regenerateApiKey(keyId: string): Promise<ApiResponse<ApiKey>> {
    return apiService.post(`${this.basePath}/api-keys/${keyId}/regenerate`);
  }

  async getApiKeyUsage(keyId: string, period: '24h' | '7d' | '30d' = '7d'): Promise<ApiResponse<{
    totalRequests: number;
    successRate: number;
    errorsByCode: Record<string, number>;
    usageByHour: Array<{ hour: string; requests: number }>;
    topEndpoints: Array<{ endpoint: string; requests: number }>;
  }>> {
    return apiService.get(`${this.basePath}/api-keys/${keyId}/usage?period=${period}`);
  }

  // User Account Management
  async getUserProfile(): Promise<ApiResponse<UserAccount>> {
    return apiService.get(`${this.basePath}/profile`);
  }

  async updateProfile(updates: {
    displayName?: string;
    avatar?: string;
    bio?: string;
    website?: string;
    location?: string;
    company?: string;
    github?: string;
    twitter?: string;
  }): Promise<ApiResponse<UserAccount>> {
    return apiService.patch(`${this.basePath}/profile`, updates);
  }

  async updatePreferences(preferences: {
    emailNotifications?: boolean;
    marketingEmails?: boolean;
    theme?: 'light' | 'dark' | 'auto';
    language?: string;
    timezone?: string;
  }): Promise<ApiResponse<UserAccount>> {
    return apiService.patch(`${this.basePath}/preferences`, preferences);
  }

  async uploadAvatar(file: File): Promise<ApiResponse<{ avatarUrl: string }>> {
    const formData = new FormData();
    formData.append('avatar', file);

    return apiService.post(`${this.basePath}/profile/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // Security
  async enable2FA(): Promise<ApiResponse<{
    secret: string;
    qrCode: string;
    backupCodes: string[];
  }>> {
    return apiService.post(`${this.basePath}/security/2fa/enable`);
  }

  async verify2FA(token: string): Promise<ApiResponse<{ enabled: boolean }>> {
    return apiService.post(`${this.basePath}/security/2fa/verify`, { token });
  }

  async disable2FA(token: string): Promise<ApiResponse<{ disabled: boolean }>> {
    return apiService.post(`${this.basePath}/security/2fa/disable`, { token });
  }

  async getSecurityEvents(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    events: Array<{
      id: string;
      type: 'login' | 'api_key_created' | 'password_changed' | 'profile_updated';
      description: string;
      ipAddress: string;
      userAgent: string;
      createdAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    return apiService.get(`${this.basePath}/security/events?page=${page}&limit=${limit}`);
  }

  // Admin Operations (admin role required)
  async getAdminMetrics(): Promise<ApiResponse<AdminMetrics>> {
    return apiService.get(`${this.basePath}/admin/metrics`);
  }

  async getAllUsers(page: number = 1, limit: number = 50, filters?: {
    role?: 'user' | 'developer' | 'admin';
    verified?: boolean;
    search?: string;
  }): Promise<ApiResponse<{
    users: UserAccount[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value.toString());
        }
      });
    }

    return apiService.get(`${this.basePath}/admin/users?${params.toString()}`);
  }

  async moderateFunction(functionId: string, action: 'approve' | 'reject', reason?: string): Promise<ApiResponse<void>> {
    return apiService.post(`${this.basePath}/admin/functions/${functionId}/moderate`, {
      action,
      reason
    });
  }

  async banUser(userId: string, reason: string, duration?: number): Promise<ApiResponse<void>> {
    return apiService.post(`${this.basePath}/admin/users/${userId}/ban`, {
      reason,
      duration // in days, undefined = permanent
    });
  }

  async unbanUser(userId: string): Promise<ApiResponse<void>> {
    return apiService.delete(`${this.basePath}/admin/users/${userId}/ban`);
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;